import {
  ChangeDetectorRef, ChangeDetectionStrategy, Component, ElementRef, EventEmitter,
  Input, NgZone, OnDestroy, Output, ViewChild,
} from '@angular/core';
import * as moment from 'moment';
import * as _ from 'lodash';
import { Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { LogsService } from './logs.service';
import { PreferencesService } from '../preferences/preferences.service';
import { segments } from '../../product-details/segments-list';
import { rollbackToast } from '../toaster/toasts';
import { getUserInitials } from '../user/user-helpers';
import {finalize, takeWhile} from 'rxjs/operators';
import {fromEvent} from 'rxjs/internal/observable/fromEvent';
import { ACTION_UPDATED, ACTION_BULK_UPDATED, ACTION_AUTO_FIX } from './logs-actions';


@Component({
  moduleId: module.id,
  selector: 'logs-component',
  templateUrl: 'logs.component.html',
  styleUrls: ['logs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LogsComponent implements OnDestroy {

  @Input() brandId: any;
  @Input() itemId: any;
  @Input() rootUrl: string = 'items';
  @Input() statusRules: string[];
  @Input() statusLookup: any[];
  @Input() allowScroll: boolean = true;
  @Input() isSmall: boolean = true;

  @Output() rollbackEnded: EventEmitter<any> = new EventEmitter<any>();
  @Output() segmentClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() fetchingLogs: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('scroll', {static: false}) scroll: ElementRef | void;

  model: any = {
    current_page: 1,
    per_page: 100,
    total: 0,
    last_page: 0,
  };
  logsData: Array<any> = [];
  isActive: boolean = true;
  wasDataRequested: boolean = false;

  private scrollHeight: number = 0;
  private logHeight: number = 80;
  private loading: boolean = false;
  private timezone: string;

  get listHeight(): number {
    return this.logsData.length * this.logHeight;
  }

  constructor(
    private logsService: LogsService,
    private ngZone: NgZone,
    private ref: ChangeDetectorRef,
    private toastrService: ToastrService,
    private preferencesService: PreferencesService,
  ) {}

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnDestroy() {
    this.isActive = false;
    this.logsData = [];
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  public fetchLogs() {
    if (this.loading) { return; }
    this.loading = true;
    this.fetchingLogs.emit(true);
    this.preferencesService.getPreferences()
    .subscribe((preferences: any) => {
      this.timezone = preferences.timezone;
      this.logsService.fetch(this.brandId, this.itemId, this.model.current_page, this.model.per_page, this.rootUrl)
      .pipe(finalize(() => this.fetchingLogs.emit(false)))
      .subscribe((response: any) => {
        this.setData(response);
        this.wasDataRequested = true;
      });
    });
  }

  public updateLogs(id?: any) {
    if (id) { this.itemId = id; }
    this.logsData = [];
    this.model.current_page = 1;
    this.fetchLogs();
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onRollBackClick(item: any) {
    if (!item.rollback_id) { return; }
    this.logsService.rollback(this.brandId, this.itemId, item.rollback_id)
    .subscribe((response: any) => {
      this.toastrService.info('', '', rollbackToast());
      this.updateLogs();
      _.defer(() => this.rollbackEnded.emit());
    });
  }

  onSegmentClick(segment: string) {
    this.segmentClicked.emit(segment);
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  private isStatus(log: any): boolean {
    if (!_.has(log, 'action.action')) { return false; }
    return log.action.action === 3;
  }

  private isNextStatus(index: any): boolean {
    if (index === this.logsData.length - 1) { return false; }
    return this.isStatus(this.logsData[index + 1]);
  }

  private isPrevStatus(index: any): boolean {
    if (index === 0) { return false; }
    return this.isStatus(this.logsData[index - 1]);
  }

  private setData(response: any, markForCheck?: boolean): void {
    _.assign(this.model, _.pick(response, ['current_page', 'total', 'last_page']));
    this.logsData = this.groupLogs(response.data);
    this.loading = false;
    this.ref.markForCheck();
  }

  // TODO: understand this usefulness
  private subscribeLoadLogsOnScroll () {
    this.ngZone.runOutsideAngular(() => {
      if (this.scroll) {
        fromEvent(this.scroll.nativeElement, 'scroll')
          .pipe(takeWhile(() => this.isActive))
          .subscribe((event: any) => {
            if ( (this.listHeight - this.scrollHeight - this.logHeight * 2) > event.currentTarget.scrollTop) { return; }
            // handle end of the scroll
            if (this.model.current_page === this.model.last_page) { return; }
            this.model.current_page += 1;
            this.fetchLogs();
          });
      }
    });
  }

  private groupLogs(logs: Array<any>) {
    // group fitments updates sequences
    const groupedLogs = _.map(logs, (log: any) => {
      if (this.isUpdatedLog(log) || this.isUpdatedBulkLog(log)) {
        // add itself copy to the innerLogs
        log.innerLogs = [{...log}];
      }
      return log;
    });

    for (let i = 1; i < groupedLogs.length; i++) {
      const isUpdatedLogs = this.isUpdatedLog(groupedLogs[i]) && this.isUpdatedLog(groupedLogs[i - 1]);
      const isBulkUpdatedLogs = this.isUpdatedBulkLog(groupedLogs[i]) && this.isUpdatedBulkLog(groupedLogs[i - 1]);
      const isSameSegment = _.get(groupedLogs[i], 'action.segment') === _.get(groupedLogs[i - 1], 'action.segment');

      // if we have 2 updated logs for the same segment following each other
      if ((isUpdatedLogs || isBulkUpdatedLogs) && isSameSegment) {
        // add log to innerLogs of the previous log
        // and remove it
        delete groupedLogs[i].innerLogs;
        groupedLogs[i - 1].innerLogs = [...groupedLogs[i - 1].innerLogs, groupedLogs[i]];
        groupedLogs.splice(i, 1);
        i--;
      }
    }

    return groupedLogs;
  }

  private isUpdatedLog(log: any): boolean {
    if (!log) { return false; }
    const action = _.get(log, 'action.action');
    return action && action === ACTION_UPDATED;
  }

  private isUpdatedBulkLog(log: any): boolean {
    if (!log) { return false; }
    const action = _.get(log, 'action.action');
    return action && action === ACTION_BULK_UPDATED;
  }

  private isFitmentsLog(log: any): boolean {
    if (!log) { return false; }
    const segment = _.get(log, 'action.segment');
    return segment && segment === 'fitments';
  }

  private isAutoFixLog(log: any): boolean {
    if (!log) { return false; }
    const action = _.get(log, 'action.action');
    return action && action === ACTION_AUTO_FIX;
  }
}
